// The sheep crest, drawn once as data so the flat SVG fallback and the 3D texture match.
// Coordinates live in a 512 x 512 box centred on 256, 256.

export type CrestCircle = [number, number, number]

export const CREST_SIZE = 512

export const CREST_RING_TEXT = 'THE BLUNTSHEEP · EST. 2023 · MALAYSIA · '

export const CREST_HEAD_OFFSET_Y = 16

export const crestWool: CrestCircle[] = [
    [256, 214, 74],
    [196, 214, 34],
    [206, 172, 34],
    [240, 150, 36],
    [276, 150, 36],
    [308, 172, 34],
    [318, 214, 34],
    [192, 252, 26],
    [320, 252, 26]
]

export const crestForelock: CrestCircle[] = [
    [236, 232, 17],
    [256, 226, 19],
    [276, 232, 17]
]

export const crestEars = [
    'M 200 246 C 170 230 136 234 114 252 C 138 268 172 270 200 262 Z',
    'M 312 246 C 342 230 376 234 398 252 C 374 268 340 270 312 262 Z'
]

export const crestFace = 'M 214 236 C 210 296 226 354 256 360 C 286 354 302 296 298 236 C 286 220 226 220 214 236 Z'

export const crestEyeLids: Array<[number, number, number, number]> = [
    [224, 276, 250, 276],
    [262, 276, 288, 276]
]

export const crestPupils = [
    'M 228 276 A 9 9 0 0 0 246 276 Z',
    'M 266 276 A 9 9 0 0 0 284 276 Z'
]

export const crestNostrils: Array<[number, number]> = [
    [244, 330],
    [268, 330]
]

export const crestMouth: [number, number, number, number] = [245, 345, 267, 345]

const INK = '#0D0D0D'
const PAPER = '#F1EFEA'

type PaintOptions = {
    ink?: string
    paper?: string
    monoFamily?: string
    displayFamily?: string
    side?: 'front' | 'back'
    relief?: boolean
}

const drawRingText = (ctx: CanvasRenderingContext2D, text: string, radius: number, fontSize: number, family: string, color: string): void => {
    ctx.save()
    ctx.fillStyle = color
    ctx.font = `700 ${fontSize}px ${family}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const characters = Array.from(text)
    const step = (Math.PI * 2) / characters.length

    characters.forEach((character, index) => {
        const angle = -Math.PI / 2 + index * step
        ctx.save()
        ctx.translate(256 + Math.cos(angle) * radius, 256 + Math.sin(angle) * radius)
        ctx.rotate(angle + Math.PI / 2)
        ctx.fillText(character, 0, 0)
        ctx.restore()
    })

    ctx.restore()
}

const drawHead = (ctx: CanvasRenderingContext2D, ink: string, paper: string): void => {
    ctx.save()
    ctx.translate(0, CREST_HEAD_OFFSET_Y)
    ctx.fillStyle = ink

    crestEars.forEach(path => ctx.fill(new Path2D(path)))

    crestWool.forEach(([x, y, r]) => {
        ctx.beginPath()
        ctx.arc(x, y, r, 0, Math.PI * 2)
        ctx.fill()
    })

    const face = new Path2D(crestFace)
    ctx.fillStyle = paper
    ctx.fill(face)
    ctx.lineWidth = 7
    ctx.strokeStyle = ink
    ctx.stroke(face)

    ctx.fillStyle = ink

    crestForelock.forEach(([x, y, r]) => {
        ctx.beginPath()
        ctx.arc(x, y, r, 0, Math.PI * 2)
        ctx.fill()
    })

    ctx.lineCap = 'round'
    ctx.lineWidth = 6

    crestEyeLids.forEach(([x1, y1, x2, y2]) => {
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.stroke()
    })

    crestPupils.forEach(path => ctx.fill(new Path2D(path)))

    crestNostrils.forEach(([x, y]) => {
        ctx.beginPath()
        ctx.ellipse(x, y, 5, 3.6, 0, 0, Math.PI * 2)
        ctx.fill()
    })

    ctx.lineWidth = 5
    ctx.beginPath()
    ctx.moveTo(crestMouth[0], crestMouth[1])
    ctx.lineTo(crestMouth[2], crestMouth[3])
    ctx.stroke()
    ctx.restore()
}

// Paints one face of the crest onto a square canvas context sized `size`.
export const paintCrest = (ctx: CanvasRenderingContext2D, size: number, options: PaintOptions = {}): void => {
    const ink = options.ink ?? INK
    const paper = options.paper ?? PAPER
    const mono = options.monoFamily ?? 'ui-monospace, monospace'
    const display = options.displayFamily ?? 'system-ui, sans-serif'
    const side = options.side ?? 'front'

    ctx.save()
    ctx.clearRect(0, 0, size, size)
    ctx.scale(size / CREST_SIZE, size / CREST_SIZE)

    ctx.fillStyle = ink
    ctx.fillRect(0, 0, CREST_SIZE, CREST_SIZE)

    ctx.strokeStyle = paper
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.arc(256, 256, 236, 0, Math.PI * 2)
    ctx.stroke()

    ctx.beginPath()
    ctx.arc(256, 256, 176, 0, Math.PI * 2)
    ctx.stroke()

    drawRingText(ctx, CREST_RING_TEXT, 206, 21, mono, paper)

    if (side === 'front') {
        ctx.fillStyle = paper
        ctx.beginPath()
        ctx.arc(256, 256, 168, 0, Math.PI * 2)
        ctx.fill()

        drawHead(ctx, ink, paper)
    }
    else {
        ctx.fillStyle = paper
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.font = `900 46px ${display}`
        ctx.fillText('BLUNT IS', 256, 192)
        ctx.fillText('LIGHT', 256, 236)
        ctx.font = `900 46px ${display}`
        ctx.fillText('BLUNT IS', 256, 288)
        ctx.fillText('TIGHT', 256, 332)
        ctx.font = `700 15px ${mono}`
        ctx.fillText('CHOOSE YOUR PATH RIGHT', 256, 380)
    }

    ctx.restore()
}
